import {
  type ApiResponse,
  apiClient,
  handleApiError,
  handleApiResponse,
} from './api'

// Types
export interface FeatureFlag {
  id: string
  name: string
  description: string | null
  enabled: boolean
  sampleRate: number | null
  createdAt: string
  updatedAt: string
  overrideCount?: number
}

export interface FeatureFlagDetailed extends FeatureFlag {
  overrides: FeatureFlagOverride[]
}

export interface FeatureFlagOverride {
  id: string
  featureFlagId: string
  organizationId: string
  organizationName: string | null
  enabled: boolean | null
  sampleRate: number | null
  createdAt: string
  updatedAt: string
}

export interface CreateFeatureFlagRequest {
  name: string
  description?: string
  enabled: boolean
  sampleRate?: number | null
}

export interface UpdateFeatureFlagRequest {
  name?: string
  description?: string
  enabled?: boolean
  sampleRate?: number | null
}

export interface CreateOverrideRequest {
  organizationId: string
  enabled?: boolean | null
  sampleRate?: number | null
}

export interface UpdateOverrideRequest {
  enabled?: boolean | null
  sampleRate?: number | null
}

// Service — thin API layer, state management handled by TanStack Query hooks
class FeatureFlagsService {
  private readonly baseURL = '/admin/feature_flags'

  async getOrganizationFlags(): Promise<Record<string, boolean>> {
    try {
      const response = await apiClient.get<{
        flags: Record<string, boolean>
      }>('/my/feature_flags')
      return response.data.flags
    } catch (error) {
      throw handleApiError(error)
    }
  }

  async getFlags(): Promise<FeatureFlag[]> {
    try {
      const response = await apiClient.get<
        ApiResponse<{ featureFlags: FeatureFlag[] }>
      >(this.baseURL)
      return handleApiResponse(response).featureFlags
    } catch (error) {
      throw handleApiError(error)
    }
  }

  async getFlag(id: string): Promise<FeatureFlagDetailed> {
    try {
      const response = await apiClient.get<
        ApiResponse<{ featureFlag: FeatureFlagDetailed }>
      >(`${this.baseURL}/${id}`)
      return handleApiResponse(response).featureFlag
    } catch (error) {
      throw handleApiError(error)
    }
  }

  async createFlag(data: CreateFeatureFlagRequest): Promise<FeatureFlag> {
    try {
      const response = await apiClient.post<
        ApiResponse<{ featureFlag: FeatureFlag }>
      >(this.baseURL, { feature_flag: data })
      return handleApiResponse(response).featureFlag
    } catch (error) {
      throw handleApiError(error)
    }
  }

  async updateFlag(
    id: string,
    data: UpdateFeatureFlagRequest
  ): Promise<FeatureFlag> {
    try {
      const response = await apiClient.patch<
        ApiResponse<{ featureFlag: FeatureFlag }>
      >(`${this.baseURL}/${id}`, { feature_flag: data })
      return handleApiResponse(response).featureFlag
    } catch (error) {
      throw handleApiError(error)
    }
  }

  async deleteFlag(id: string): Promise<void> {
    try {
      await apiClient.delete(`${this.baseURL}/${id}`)
    } catch (error) {
      throw handleApiError(error)
    }
  }

  async getOverrides(flagId: string): Promise<FeatureFlagOverride[]> {
    try {
      const response = await apiClient.get<
        ApiResponse<{ overrides: FeatureFlagOverride[] }>
      >(`${this.baseURL}/${flagId}/overrides`)
      return handleApiResponse(response).overrides
    } catch (error) {
      throw handleApiError(error)
    }
  }

  async createOverride(
    flagId: string,
    data: CreateOverrideRequest
  ): Promise<FeatureFlagOverride> {
    try {
      const response = await apiClient.post<
        ApiResponse<{ override: FeatureFlagOverride }>
      >(`${this.baseURL}/${flagId}/overrides`, { override: data })
      return handleApiResponse(response).override
    } catch (error) {
      throw handleApiError(error)
    }
  }

  async updateOverride(
    flagId: string,
    overrideId: string,
    data: UpdateOverrideRequest
  ): Promise<FeatureFlagOverride> {
    try {
      const response = await apiClient.patch<
        ApiResponse<{ override: FeatureFlagOverride }>
      >(`${this.baseURL}/${flagId}/overrides/${overrideId}`, {
        override: data,
      })
      return handleApiResponse(response).override
    } catch (error) {
      throw handleApiError(error)
    }
  }

  async deleteOverride(flagId: string, overrideId: string): Promise<void> {
    try {
      await apiClient.delete(
        `${this.baseURL}/${flagId}/overrides/${overrideId}`
      )
    } catch (error) {
      throw handleApiError(error)
    }
  }
}

export const featureFlagsService = new FeatureFlagsService()
